import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAbbreviationBadge,
  MiCard,
  MiDetailFields,
} from "@miplatform/components";
import { ENTITY_NAMES, INPUT_TYPES } from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { IHome, IInspectionTemplate, ISelectOption } from "@miplatform/types";
import { getContrastColor, isEmpty } from "@miplatform/utils";
import { mdiAccount, mdiTools } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { HomeInformation } from "../OrderTemplateSelection/HomeInformation/HomeInformation";
import { useInspectionTemplateSelectionViewModel } from "./useInspectionTemplateSelectionViewModel";

const INITIAL_VALUES = {
  trade: "All Trades",
};

function InspectionTemplate({
  inspectionTemplate,
  active,
  onClick,
  testID,
}: {
  inspectionTemplate: IInspectionTemplate;
  active: boolean;
  onClick: () => void;
  testID: string;
}) {
  const { name, color, shortName, abbreviation } = inspectionTemplate;

  const theme = useTheme();
  const { classes } = useInspectionTemplateStyles({ color, active });

  return (
    <Box
      className={classes.inspectionTemplateContainer}
      onClick={onClick}
      data-testid={testID}
    >
      <MiAbbreviationBadge
        abbreviation={abbreviation ?? shortName ?? "---"}
        backgroundColor={color}
      />
      <Box flex={0.8}>
        <Typography
          variant="caption"
          fontWeight={theme.typography.fontWeightBold}
        >
          {name}
        </Typography>
      </Box>
    </Box>
  );
}

export function InspectionTemplateSelection({
  home,
  goBack = () => {},
  selectedInspectionTemplate,
  handleInspectionTemplateSelection = () => {},
}: IInspectionTemplateSelectionProps) {
  const theme = useTheme();

  const {
    isLoading,
    trades = [],
    inspectionTemplates = [],
    loadAllInspectionTemplates,
    getTrades,
  } = useInspectionTemplateSelectionViewModel();

  const tradesFromInspectionTemplates: ISelectOption[] = [];
  inspectionTemplates.forEach((template: IInspectionTemplate) => {
    if (
      !tradesFromInspectionTemplates.find(
        (existingTrade) => existingTrade.value === template?.tradeId
      )
    ) {
      tradesFromInspectionTemplates.push({
        value: template?.tradeId ?? null,
        label:
          trades.find((trade) => trade.id === template?.tradeId)?.name ?? "",
      });
    }
  });

  const tradeOptions = [
    { label: "All Trades", value: "All Trades" },
    ...tradesFromInspectionTemplates.map(({ label, value }) => ({
      label,
      value,
    })),
  ];

  useEffect(() => {
    loadAllInspectionTemplates();
    getTrades();
  }, []);

  const detailFields = [
    {
      label: "Trade",
      name: "trade",
      fieldType: INPUT_TYPES.SELECT,
      disabled: isLoading,
      options: tradeOptions,
      handleChange: (e: React.ChangeEvent<{ value: unknown }>) => {
        const { value } = e.target;

        edit.update({
          trade: value,
          inspectionTemplates:
            value === INITIAL_VALUES.trade
              ? inspectionTemplates
              : (inspectionTemplates as IInspectionTemplate[]).filter(
                  (template) => template.tradeId === value
                ),
        });
      },
    },
  ];

  const edit = useEdit(
    { ...INITIAL_VALUES, inspectionTemplates },
    detailFields
  );

  const { classes } = useStyles();

  return (
    <Box className={classes.root}>
      <HomeInformation home={home} onRightIconClick={goBack} />
      <Box className={classes.container}>
        <MiCard
          title={`${ENTITY_NAMES.INSPECTION} Trade`}
          leftIcon={mdiTools}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <MiDetailFields detailFields={detailFields} edit={edit} />
        </MiCard>
        <MiCard
          title={ENTITY_NAMES.INSPECTION_TEMPLATES}
          leftIcon={mdiAccount}
          containerStyles={{ padding: theme.spacing(2) }}
        >
          <Box className={classes.inspectionTemplates}>
            {!isEmpty(edit.getValue("inspectionTemplates")) ? (
              edit
                .getValue("inspectionTemplates")
                .filter((inspectionTemplate: IInspectionTemplate) => {
                  const selectedTrade = edit.getValue("trade");

                  const matchesTrade =
                    selectedTrade === INITIAL_VALUES.trade ||
                    inspectionTemplate.tradeId === selectedTrade;

                  return matchesTrade;
                })
                .map((inspectionTemplate: IInspectionTemplate, i: number) => (
                  <InspectionTemplate
                    key={inspectionTemplate.id}
                    inspectionTemplate={inspectionTemplate}
                    active={
                      selectedInspectionTemplate?.id === inspectionTemplate.id
                    }
                    onClick={() => {
                      handleInspectionTemplateSelection(inspectionTemplate);
                    }}
                    testID={`inspection-template-${i}`}
                  />
                ))
            ) : (
              <Typography className={classes.placeholderText}>
                No {ENTITY_NAMES.INSPECTION_TEMPLATES} Found
              </Typography>
            )}
          </Box>
        </MiCard>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flexGrow: 1,
    height: "100%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  inspectionTemplates: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  placeholderText: {
    color: theme.palette.grey.main,
    textAlign: "center",
    width: "100%",
    padding: theme.spacing(2),
  },
}));

const useInspectionTemplateStyles = makeStyles<{
  color?: string;
  active?: boolean;
}>()((theme, { color, active }) => ({
  inspectionTemplateContainer: {
    flex: 1,
    backgroundColor: theme.palette.common.white,
    borderRadius: 4,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    cursor: "pointer",
    boxShadow: theme.shadows[1],
    border: `1px solid ${active ? theme.palette.cyan.dark : "transparent"}`,

    "&:hover": {
      opacity: 0.8,
      border: `1px solid ${theme.palette.cyan.dark}`,
    },
  },
  shortNameContainer: {
    backgroundColor: color,
    borderRadius: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 32,
    height: 32,
    color: color ? getContrastColor(color) : undefined,
  },
}));

interface IInspectionTemplateSelectionProps {
  home?: IHome;
  goBack?: () => void;
  handleInspectionTemplateSelection: (
    inspectionTemplate: IInspectionTemplate
  ) => void;
  selectedInspectionTemplate: IInspectionTemplate;
}
